import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { PlayerRole, type PensieveQuestionLevel } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { useGameWord } from '../../hooks/useGameWord';
import { getRoleConfig } from '../../utils/roleConfig';
import { getLanguage } from '@deadpan/shared';
import { usePersistedPensieveLevelRange } from '../../hooks/usePersistedPensieveLevelRange';
import { DeadpanAnnouncementLine } from './DeadpanAnnouncementLine';

const LEVELS: PensieveQuestionLevel[] = [0, 1, 2, 3];

export const DeadpanRoleReveal: React.FC = () => {
  const { socket, gameState, currentRoom, myRole, myPlayerId } = useSocket();
  const t = useTranslations();
  const { word, definition } = useGameWord(gameState);
  const { range, setMin, setMax } = usePersistedPensieveLevelRange();
  const isZh = getLanguage() === 'zh';

  if (!gameState || !currentRoom) {
    return null;
  }

  const roleConfig = getRoleConfig(t);
  const isHost = currentRoom.hostId === myPlayerId;
  
  // Find Fact-Speaker to check if detective voted correctly
  const factSpeaker = currentRoom.players.find(p => p.role === PlayerRole.FACT_SPEAKER);
  const isDetective = myRole === PlayerRole.DETECTIVE;
  const myVote = myPlayerId ? gameState.votes[myPlayerId] : null;
  const punishment = gameState.deadpanPunishment;
  const votedCorrectly = isDetective && (
    punishment
      ? punishment.reason === 'detective_correct'
      : Boolean(myVote && factSpeaker && myVote === factSpeaker.id)
  );
  const votedIncorrectly = isDetective && (
    punishment
      ? punishment.reason === 'detective_wrong'
      : Boolean(myVote && factSpeaker && myVote !== factSpeaker.id)
  );
  const chosenPlayer = punishment
    ? currentRoom.players.find((player) => player.id === punishment.respondentId)
    : null;
  const isChosenPlayer = Boolean(myPlayerId && punishment && punishment.respondentId === myPlayerId);
  const punishmentQuestionText = punishment?.question
    ? (isZh ? punishment.question.questionZh : punishment.question.question)
    : '';

  const handlePunishmentDraw = () => {
    if (!socket || !isChosenPlayer || !punishment) return;
    socket.emit('client:deadpan-punishment-draw', range);
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <div className="w-full text-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 px-2">
          {t.games.deadpan.rolesRevealed}
        </h2>
        <p className="text-base sm:text-lg text-gray-700 mb-1 px-2">{t.games.deadpan.theWord}</p>
        <div className="w-full overflow-x-auto">
          <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 mb-2 sm:mb-4 px-2 whitespace-nowrap">
            {word}
          </p>
        </div>
        <DeadpanAnnouncementLine />
      </div>

      <div className="w-full space-y-3 sm:space-y-4 mb-4 sm:mb-6">
        {currentRoom.players.map((player) => {
          if (!player.role) return null;
          const config = roleConfig[player.role];
          const voteCount = Object.values(gameState.votes).filter(id => id === player.id).length;
          const isMyPlayer = player.id === myPlayerId;
          
          return (
            <div
              key={player.id}
              className={`p-4 sm:p-6 rounded-xl border-2 bg-gradient-to-r ${config.gradient} text-white`}
            >
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-4">
                  <span className="text-3xl sm:text-4xl">{config.emoji}</span>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold">{player.name}</h3>
                    <p className="text-base sm:text-lg opacity-90">{config.name}</p>
                    {isDetective && isMyPlayer && (votedCorrectly || votedIncorrectly) && (
                      <p className={`text-sm mt-2 font-semibold ${
                        votedCorrectly ? 'text-green-200' : 'text-red-200'
                      }`}>
                        {votedCorrectly ? '✓ ' + t.games.deadpan.correctGuess : '✗ ' + t.games.deadpan.incorrectGuess}
                      </p>
                    )}
                  </div>
                </div>
                {voteCount > 0 && (
                  <div className="text-right">
                    <p className="text-sm opacity-90">{voteCount} {voteCount === 1 ? t.games.deadpan.vote : t.games.deadpan.votes}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {isDetective && (
        <div className={`w-full mb-4 sm:mb-6 p-4 sm:p-6 rounded-xl border-2 ${
          votedCorrectly ? 'bg-green-50 border-green-300' :
          votedIncorrectly ? 'bg-red-50 border-red-300' :
          'bg-gray-50 border-gray-300'
        }`}>
          <p className={`text-center font-semibold text-base sm:text-lg ${
            votedCorrectly ? 'text-green-800' : votedIncorrectly ? 'text-red-800' : 'text-gray-800'
          }`}>
            {votedCorrectly ? '🎉 ' + t.games.deadpan.youGuessedCorrectly :
             votedIncorrectly ? '😔 ' + t.games.deadpan.youGuessedIncorrectly :
             t.games.deadpan.youDidNotVote}
          </p>
        </div>
      )}

      {definition && (
        <div className="w-full bg-green-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-green-300">
          <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center gap-2 text-green-800">
            <span className="text-lg sm:text-xl">📢</span>
            {t.games.deadpan.realDefinition}
          </h3>
          <div className="text-lg sm:text-xl font-semibold p-3 sm:p-4 rounded-lg bg-green-100 text-green-900 text-center">
            {definition}
          </div>
        </div>
      )}

      {punishment && (
        <div className="w-full bg-purple-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-purple-300">
          <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center gap-2 text-purple-800">
            <span className="text-lg sm:text-xl">🔮</span>
            {t.games.deadpan.punishmentTitle}
          </h3>
          <p className="text-sm sm:text-base text-purple-900 mb-3">
            {punishment.reason === 'detective_correct'
              ? t.games.deadpan.punishmentDescriptionCorrect
              : t.games.deadpan.punishmentDescriptionWrong}
          </p>
          <p className="text-sm sm:text-base text-purple-900 mb-4">
            <span className="font-semibold">{t.games.deadpan.punishmentChosenPlayerLabel}:</span> {chosenPlayer?.name ?? '—'}
          </p>

          {isChosenPlayer && (
            <div className="rounded-lg border border-purple-200 bg-white p-3 sm:p-4 mb-3">
              <p className="text-sm font-semibold text-purple-800 mb-3">{t.games.deadpan.punishmentYouAreChosen}</p>
              <div className="flex flex-col gap-3 mb-2">
                <span className="text-sm font-medium text-gray-700">{t.games.deadpan.punishmentChooseLevelLabel}</span>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <label htmlFor="deadpan-punishment-level-min" className="text-sm font-medium text-gray-700">
                      {t.games.pensieve.levelMinLabel}
                    </label>
                    <select
                      id="deadpan-punishment-level-min"
                      value={range.min}
                      onChange={(e) => setMin(Number(e.target.value) as PensieveQuestionLevel)}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 min-w-[4rem]"
                    >
                      {LEVELS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label htmlFor="deadpan-punishment-level-max" className="text-sm font-medium text-gray-700">
                      {t.games.pensieve.levelMaxLabel}
                    </label>
                    <select
                      id="deadpan-punishment-level-max"
                      value={range.max}
                      onChange={(e) => setMax(Number(e.target.value) as PensieveQuestionLevel)}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 min-w-[4rem]"
                    >
                      {LEVELS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePunishmentDraw}
                  className="w-full min-h-[2.75rem] px-6 py-3 text-base bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700"
                >
                  {t.games.deadpan.punishmentDrawButton}
                </button>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-purple-200 bg-white p-3 sm:p-4 mb-3">
            <p className="text-sm font-semibold text-purple-800 mb-2">{t.games.deadpan.punishmentQuestionLabel}</p>
            {punishment.question ? (
              <>
                <p className="text-sm text-purple-600 mb-1">
                  {t.games.pensieve.levelLabel}: {punishment.question.level}
                </p>
                <p className="text-base sm:text-lg font-medium text-purple-900 break-words">{punishmentQuestionText}</p>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                {isChosenPlayer ? t.games.deadpan.punishmentNoQuestionYet : t.games.deadpan.punishmentWaitingChosenDraw}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="w-full mt-4 sm:mt-6 pt-4 sm:pt-6 border-t-2 border-gray-200">
        {isHost ? (
          <button
            onClick={() => socket?.emit('client:start-new-game')}
            className="w-full text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg transition-all shadow-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 hover:shadow-xl transform hover:-translate-y-0.5"
          >
            🎮 {t.ui.startNewGame}
          </button>
        ) : (
          <p className="text-center text-gray-500 text-xs sm:text-sm">{t.games.deadpan.waitingForHostToStartNewGame}</p>
        )}
      </div>
    </div>
  );
};

